import { FormLayout, FormLayoutProps } from "./FormLayout";
import { Box } from "@mui/material";
import { dFlex } from "src/styles/sx";
import { AnyError } from "src/errorHandling/anyError";
import { AavoButton } from "../buttons/AavoButton";
import i18n from "i18next";
import { UseFormHandleSubmit } from "react-hook-form";
import { AsyncButton } from "../buttons/AsyncButton";
import { FormCommonProps, FormSubmitResult } from "src/components/common/forms/types.ts";
import { ErrorDialogContext } from "src/components/common/dialogs/errorDialog/ErrorDialogProvider.tsx";

import { useContextOrThrow } from "src/utils/useContextOrThrow.tsx";

export interface FormBaseProps<TFormFields extends object, TResult = void>
	extends FormLayoutProps,
		Pick<FormCommonProps<TResult>, "onCompleted"> {
	handleSubmit: UseFormHandleSubmit<TFormFields>;
	submit: (formData: TFormFields) => Promise<FormSubmitResult<TResult>>;
	submitLabel?: string;
	isDirty: boolean;
}

export const AavoForm = <TFormFields extends object, TResult>(
	{
		handleSubmit,
		submit,
		onCompleted,
		isDirty,
		children,
		columnMinWidth,
		columns = 1,
		gap = 2,
		fitContent,
		layoutSx,
		submitLabel
	}: FormBaseProps<TFormFields, TResult>) => {

	const showErrorDialog = useContextOrThrow(ErrorDialogContext);

	const executeSubmit = async () => {
		await handleSubmit(
			async (formData: TFormFields) => {
				try {
					const submitResult = await submit(formData);
					if (submitResult === "interrupted") {
						return;
					}
					onCompleted?.({ type: "success", value: submitResult });
				} catch (e) {
					onCompleted?.({ type: "error", error: e as AnyError });
					showErrorDialog(e as AnyError);
				}
			},
			(errors) => {
				console.info("Form has errors", errors);
			}
		)();
	};

	const handleCancel = async () => {
		onCompleted?.({ type: "cancel", isEdited: isDirty });
	};

	const onFormKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
		if (e.key === "Enter" && e.ctrlKey && isDirty) {
			await executeSubmit();
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				flex: 1
			}}
			onKeyDown={onFormKeyDown}
		>
			<FormLayout columns={columns}
						columnMinWidth={columnMinWidth}
						gap={gap}
						fitContent={fitContent}
						layoutSx={layoutSx}>
				{children}
			</FormLayout>
			<Box
				sx={{
					display: "flex",
					backgroundColor: theme => theme.palette.grey[200],
					minHeight: "2.5rem",
					justifyContent: "flex-end"
				}}
			>
				<AavoButton color={"secondary"} onClick={handleCancel}>
					{i18n.t("cancel")}
				</AavoButton>
				<Box sx={dFlex}>
					<AsyncButton onClick={executeSubmit}>
						{submitLabel ?? i18n.t("save")}
					</AsyncButton>
				</Box>
			</Box>
		</Box>
	);
};
